import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FieldWrapper, type FormlyFieldConfig } from '@ngx-formly/core';

import { clearableIsReadOnly } from '../clearable-field.type';
import { ReadonlyFieldRowComponent } from './readonly-field-row.component';
import { readonlyPresenceValue } from './readonly.types';

/**
 * Outermost wrapper of every field read mode can show as a row (#21239).
 *
 * The `@else` branch is what makes read mode a rendering promise instead of a styling one:
 * Formly only builds the type component once the `#fieldComponent` container EXISTS — it
 * watches the wrapper's dynamic view query (`renderField`, ngx-formly-core.mjs:1958-1990) — so
 * inside a false `@if` no control is created at all. Nothing to focus, nothing to type in,
 * nothing for a screen reader to announce as editable. The base class' STATIC query
 * (`_staticContent`, :2875-2879) stays undefined here, which is fine: only the dynamic one
 * drives the render.
 *
 * PAGE mode (read vs edit) switches by rebuilding the field tree — `DetailsService` does that,
 * stamping `props.readonly` statically via `markLeavesReadonly` — so in the app today the flag is
 * fixed for a given tree. It is deliberately NOT derived from rights or conditional rules: those
 * produce a DISABLED input in edit mode instead (`form.service.ts`, decision of 2026-09-10), which
 * this wrapper renders by building the type component as usual. `#dropDestroyedComponentRefs`
 * below stays because the `@if` can still tear a view down whenever `props.readonly` /
 * `props.readOnly` is flipped at runtime by anything else — and because Formly never clears
 * `_componentRefs` for a view an `@if` destroyed.
 */
@Component({
  selector: 'formly-wrapper-readonly',
  imports: [ReadonlyFieldRowComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // display: contents — the wrapper must not become a box between the grid and the field, or
  // every width class on `<formly-field>` would stop reaching the row.
  styles: `
    :host {
      display: contents;
    }
  `,
  template: `
    @if (isReadonly()) {
      <dd-readonly-field-row [field]="field" [value]="value()" />
    } @else {
      <ng-container #fieldComponent></ng-container>
    }
  `,
})
export class ReadonlyWrapper extends FieldWrapper {
  /** Last value the template rendered, so a runtime flip can be detected without a signal. */
  #renderedReadonly: boolean | undefined;

  /**
   * Methods, not computed signals: `props` is a plain object that Formly mutates in place, so a
   * signal would never see the write. Formly marks the field view for check itself after every
   * expression pass, which is what re-evaluates these.
   */
  isReadonly(): boolean {
    const readonly = clearableIsReadOnly(this.props);
    if (readonly !== this.#renderedReadonly) {
      this.#renderedReadonly = readonly;
      this.#dropDestroyedComponentRefs();
    }
    return readonly;
  }

  /**
   * The answer the row shows and decides "Ontbreekt" on. `readonlyPresenceValue`, not the bare
   * control value: an `upload` field keeps its files on the entry, not in its control (task 21243).
   */
  value(): unknown {
    return readonlyPresenceValue(this.field);
  }

  /**
   * Drops the component refs of views the `@if` tore down (#21239).
   *
   * Formly registers every wrapper and type component it renders on `field._componentRefs`
   * (`attachComponentRef`, ngx-formly-core.mjs:2017-2023) and only ever clears that list when the
   * FIELD is destroyed (`FormlyField.ngOnDestroy` / `resetRefs`, :2081-2091). The `@if` above
   * destroys the container instead, which makes Formly's `fieldComponent` observer rebuild the
   * inner chain and PUSH a fresh set on the next edit pass. Measured without this cleanup: 19 refs
   * after five round trips, 15 of them destroyed — and the list is not inert. `markFieldForCheck`
   * walks all of them on every check (:291-299) and `getFieldComponentInstance` hands out
   * `_componentRefs.slice(-1)[0]` (:1271-1276), so a destroyed view can become the instance that
   * answers for the field.
   *
   * Runs on the TRANSITION only — the flip is the moment a view was torn down or is about to be —
   * and never removes the live one: the ref of the view being destroyed in this very pass is not
   * marked destroyed yet, and it is collected on the next flip.
   */
  #dropDestroyedComponentRefs(): void {
    const field = this.field as FormlyFieldConfig & { _componentRefs?: ComponentRefLike[] };
    const refs = field._componentRefs;
    if (!refs?.length) {
      return;
    }

    const live = refs.filter(ref => !isDestroyed(ref));
    if (live.length !== refs.length) {
      field._componentRefs = live;
    }
  }
}

/** A `ComponentRef` (view behind `hostView`) or a bare `ViewRef` — Formly stores both. */
interface ComponentRefLike {
  hostView?: { destroyed?: boolean };
  destroyed?: boolean;
}

function isDestroyed(ref: ComponentRefLike): boolean {
  return (ref.hostView?.destroyed ?? ref.destroyed) === true;
}
